import { crearMovimiento } from "./flujoCaja";
import { client } from "./supabase";

export const getProveedoresFactoring = async (pais) => {
  const { data, error } = await client
    .from("proveedores_factoring")
    .select("*")
    .eq("pais", pais)
    .eq("activo", true)
    .order("nombre");
  if (error) throw error;
  return data || [];
};

export const getOperacionesFactoring = async (pais, filters = {}) => {
  let query = client
    .from("operaciones_factoring")
    .select(
      `
      *,
      factura:facturas_sii_emitidas(folio, razon_social_receptor, monto_total, fecha_emision, cliente_id),
      proveedor:proveedores_factoring(nombre, tipo, tasa_habitual),
      moras:operaciones_factoring_mora(id, monto_mora, estado)
    `
    )
    .eq("pais", pais)
    .order("fecha_operacion", { ascending: false });

  if (filters.estado) query = query.eq("estado", filters.estado);
  if (filters.fechaDesde) query = query.gte("fecha_operacion", filters.fechaDesde);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export const crearOperacionFactoring = async (operacion) => {
  // Forzamos estado="activa" — la DB tiene default='activo' (masculino) que el código
  // de la página filtra como activa (femenino). Ver migración 20260509_factoring_campos.sql
  // que ajusta el default; este override garantiza consistencia hasta que se aplique.
  const { data, error } = await client
    .from("operaciones_factoring")
    .insert({ ...operacion, estado: "activa" })
    .select()
    .single();
  if (error) throw error;

  const { error: errorEstado } = await client
    .from("facturas_sii_emitidas")
    .update({ estado_factoring: "cedida" })
    .eq("id", operacion.factura_sii_emitida_id);
  if (errorEstado) throw errorEstado;

  return data;
};

export const actualizarOperacionFactoring = async (id, cambios) => {
  const { data, error } = await client
    .from("operaciones_factoring")
    .update({ ...cambios, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

// Eliminar definitivamente una operación. Solo procede si ya está anulada — la
// constraint .eq("estado","anulada") en el DELETE evita borrar operaciones activas
// o liquidadas por error.
export const eliminarOperacionFactoring = async (id) => {
  const { error } = await client.from("operaciones_factoring").delete().eq("id", id).eq("estado", "anulada");
  if (error) throw error;
};

// Anular una operación de factoring. Revierte estado_factoring en la factura a null
// para que vuelva a aparecer en "Por cobrar" en FlujoCajaV2.
export const anularOperacionFactoring = async (operacionId, facturaSiiEmitidaId) => {
  const { error } = await client
    .from("operaciones_factoring")
    .update({ estado: "anulada", updated_at: new Date().toISOString() })
    .eq("id", operacionId);
  if (error) throw error;

  const { error: errFactura } = await client
    .from("facturas_sii_emitidas")
    .update({ estado_factoring: null })
    .eq("id", facturaSiiEmitidaId);
  if (errFactura) throw errFactura;
};

export const getMorasOperacion = async (operacionFactoringId) => {
  const { data, error } = await client
    .from("operaciones_factoring_mora")
    .select("*")
    .eq("operacion_factoring_id", operacionFactoringId)
    .order("fecha_notificacion", { ascending: false });
  if (error) throw error;
  return data || [];
};

export const crearMoraFactoring = async ({
  operacion_factoring_id,
  pais,
  monto_mora,
  fecha_notificacion,
  notas,
  created_by,
}) => {
  const { data, error } = await client
    .from("operaciones_factoring_mora")
    .insert([
      {
        operacion_factoring_id,
        pais,
        monto_mora,
        fecha_notificacion,
        notas,
        created_by,
        estado: "pendiente",
      },
    ])
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const pagarMoraFactoring = async (moraId, fechaPagoMora) => {
  const { data, error } = await client
    .from("operaciones_factoring_mora")
    .update({ estado: "pagada", fecha_pago_mora: fechaPagoMora })
    .eq("id", moraId)
    .select()
    .single();
  if (error) throw error;
  return data;
};

// Todas las moras pendientes de un país (para FlujoCajaV2 → bloque Créditos).
export const getMorasPendientes = async (pais) => {
  const { data, error } = await client
    .from("operaciones_factoring_mora")
    .select(
      `
      *,
      operacion:operaciones_factoring(
        id, monto_factura, fecha_cobro_final,
        factura:facturas_sii_emitidas(folio, razon_social_receptor)
      )
    `
    )
    .eq("pais", pais)
    .eq("estado", "pendiente")
    .order("fecha_notificacion");
  if (error) throw error;
  return data || [];
};

/**
 * Liquida una operación de factoring. Hace 3 cosas:
 *   1. UPDATE operaciones_factoring → estado='liquidada' + fecha_pago_real
 *   2. UPDATE facturas_sii_emitidas → estado_factoring='liquidada'
 *   3. INSERT movimientos_caja → cobro del remanente (monto_factura - capital_adelantado)
 *
 * El movimiento bancario real del remanente se vincula después desde FlujoCajaV2 cuando
 * el usuario asocia el depósito de Clay correspondiente.
 *
 * @param {object} operacion - operación cargada vía getOperacionesFactoring (debe traer factura embed)
 * @param {object} options
 * @param {string} options.pais - "Chile" | "Perú"
 * @param {string} [options.fechaPagoReal] - YYYY-MM-DD; default: hoy
 * @param {string} [options.monedaPrincipal="CLP"] - moneda del cobro (CLP/PEN)
 */
export const liquidarOperacionFactoring = async (
  operacion,
  { pais, fechaPagoReal, monedaPrincipal = "CLP", montoRemanente } = {}
) => {
  const fecha = fechaPagoReal || new Date().toISOString().split("T")[0];

  const { data, error } = await client
    .from("operaciones_factoring")
    .update({ estado: "liquidada", fecha_pago_real: fecha, updated_at: new Date().toISOString() })
    .eq("id", operacion.id)
    .select()
    .single();
  if (error) throw error;

  if (operacion.factura_sii_emitida_id) {
    const { error: errFactura } = await client
      .from("facturas_sii_emitidas")
      .update({ estado_factoring: "liquidada" })
      .eq("id", operacion.factura_sii_emitida_id);
    if (errFactura) throw errFactura;
  }

  const remanente = montoRemanente ?? Number(operacion.monto_factura || 0) - Number(operacion.capital_adelantado || 0);
  if (remanente > 0) {
    await crearMovimiento({
      pais,
      tipo: "cobro",
      fecha,
      monto: remanente,
      moneda: monedaPrincipal,
      estado: "confirmado",
      descripcion: `Remanente factoring — Folio ${operacion.factura?.folio || operacion.factura_sii_emitida_id}`,
      entidad_tipo: "cliente",
      entidad_id: operacion.factura?.cliente_id || null,
      categoria_pago: "cobro_factura",
      origen: "manual",
    });
  }

  return data;
};
